import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery, pyrotchBaseUrl } from "../axios/AxiosInstance";

export type ProcurementRequestStatus =
  | "REQUISITION_CREATED"
  | "IN_REVIEW"
  | "ORDERED"
  | "RECEIVED"
  | "REJECTED";

export interface ProcurementRequestInput {
  prNumber: string;
  itemName: string;
  quantity: number | string | null;
  unit?: string;
  partCode?: string;
  itemLink?: string;
  projectWbs?: string;
  departmentVertical?: "CD" | "CCR" | "";
  requirementDate?: string;
  vendor?: string;
  purpose?: string;
  remarks?: string;
  toEmails?: string[];
  ccEmails?: string[];
  batchId?: string;
  status?: ProcurementRequestStatus;
}

export interface ProcurementRequestRecord extends ProcurementRequestInput {
  id: number;
  requestedBy?: number;
  requestedByName?: string;
  batchId?: string;
  createdDate?: string;
  lastModifiedDate?: string;
}

export interface ProcurementRequestResponse {
  content: ProcurementRequestRecord[];
  currentPage: number;
  totalItems: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

export interface BulkProcurementRequestPayload {
  requests: ProcurementRequestInput[];
  files?: File[];
}

export const ProcurementApi = createApi({
  baseQuery: axiosBaseQuery({
    baseUrl: pyrotchBaseUrl + "v1/",
  }),
  reducerPath: "procurementApi",
  tagTypes: ["ProcurementRequest"],
  endpoints: (build) => ({
    getProcurementRequests: build.query<
      ProcurementRequestResponse,
      { page?: number; size?: number; search?: string; status?: ProcurementRequestStatus | "" }
    >({
      query: ({ page = 0, size = 10, search, status } = {}) => ({
        url: "procurement-requests",
        method: "GET",
        params: {
          page,
          size,
          ...(search ? { search } : {}),
          ...(status ? { status } : {}),
        },
      }),
      providesTags: ["ProcurementRequest"],
    }),
    createProcurementRequest: build.mutation<any, ProcurementRequestInput>({
      query: (payload) => ({
        url: "procurement-requests",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["ProcurementRequest"],
    }),
    createBulkProcurementRequests: build.mutation<any, ProcurementRequestInput[] | BulkProcurementRequestPayload>({
      query: (payload) => {
        if (Array.isArray(payload)) {
          return {
            url: "procurement-requests/bulk",
            method: "POST",
            data: payload,
          };
        }

        if (!payload.files || payload.files.length === 0) {
          return {
            url: "procurement-requests/bulk",
            method: "POST",
            data: payload.requests,
          };
        }

        const formData = new FormData();
        formData.append("request", JSON.stringify(payload.requests));
        formData.append("requests", JSON.stringify(payload.requests));
        payload.files.forEach((file) => formData.append("files", file));
        return {
          url: "procurement-requests/bulk",
          method: "POST",
          data: formData,
        };
      },
      invalidatesTags: ["ProcurementRequest"],
    }),
    updateProcurementRequest: build.mutation<any, { id: number; payload: ProcurementRequestInput }>({
      query: ({ id, payload }) => ({
        url: `procurement-requests/${id}`,
        method: "PUT",
        data: payload,
      }),
      invalidatesTags: ["ProcurementRequest"],
    }),
    deleteProcurementRequest: build.mutation<any, number>({
      query: (id) => ({
        url: `procurement-requests/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ProcurementRequest"],
    }),
  }),
});

export const {
  useGetProcurementRequestsQuery,
  useCreateProcurementRequestMutation,
  useCreateBulkProcurementRequestsMutation,
  useUpdateProcurementRequestMutation,
  useDeleteProcurementRequestMutation,
} = ProcurementApi;
